import { Link } from "react-router-dom";
import { ChevronDown, Instagram, Lock, Mail, ShieldCheck } from "lucide-react";
import { homeV2Asset } from "../../utils/homeV2Assets";
import "../../styles/pmV2.css";
import "./PublicPageFooter.css";

const HOME_FOOTER_SUPPORT_EMAIL = "suporte@privatemuse.com";

const FOOTER_LINK_GROUPS = [
  {
    title: "Explorar",
    links: [
      { to: "/acompanhantes-virtuais/", label: "Descobrir perfis" },
      { to: "/acompanhantes-virtuais/sao-paulo/", label: "São Paulo" },
      { to: "/acompanhantes-virtuais/rio-de-janeiro/", label: "Rio de Janeiro" },
      { to: "/acompanhantes-virtuais/brasilia/", label: "Brasília" },
      { to: "/como-funciona/", label: "Como funciona" },
    ],
  },
  {
    title: "Privacidade & segurança",
    mobileTitle: "Segurança",
    links: [
      {
        to: "/termos-de-uso-e-politica-de-privacidade/",
        label: "Política de Privacidade",
      },
      { to: "/termos-de-uso-e-politica-de-privacidade/", label: "Termos de Uso" },
      { to: "/acompanhantes-virtuais/perfis-verificados/", label: "Verificação de perfis" },
      { to: "/termos-de-uso-e-politica-de-privacidade/", label: "LGPD - Lei 13.709/18" },
    ],
  },
  {
    title: "Suporte",
    links: [
      { to: "/contato/", label: "Central de ajuda" },
      { to: "/contato/", label: "FAQ" },
      { to: "/contato/", label: "Denunciar perfil" },
      { href: `mailto:${HOME_FOOTER_SUPPORT_EMAIL}`, label: HOME_FOOTER_SUPPORT_EMAIL },
    ],
  },
  {
    title: "Acesso",
    links: [
      { to: "/login", label: "Entrar" },
      { to: "/cadastro/?tab=cliente", label: "Criar conta" },
      { to: "/cadastro-acompanhante/", label: "Anunciar meu perfil" },
      { to: "/como-funciona/", label: "Saiba mais" },
    ],
  },
];

const FOOTER_LEGAL_LINKS = [
  { to: "/termos-de-uso-e-politica-de-privacidade/", label: "Termos e política de privacidade" },
  { to: "/contato/", label: "Contato" },
  { to: "/mapa-do-site/", label: "Mapa do site" },
];

function PublicPageFooterLink({ link }) {
  if (link.href) {
    return (
      <a href={link.href} className="home-v2-footer__link">
        {link.label}
      </a>
    );
  }

  return (
    <Link to={link.to} className="home-v2-footer__link">
      {link.label}
    </Link>
  );
}

function PublicPageFooterGroup({ group }) {
  return (
    <div className="home-v2-footer__group">
      <h3 className="home-v2-footer__heading">{group.title}</h3>
      <div className="home-v2-footer__links">
        {group.links.map((link) => (
          <PublicPageFooterLink key={`${group.title}-${link.label}`} link={link} />
        ))}
      </div>
    </div>
  );
}

function PublicPageFooterAccordionGroup({ group }) {
  return (
    <details className="home-v2-footer__accordion">
      <summary>
        <span>{group.mobileTitle || group.title}</span>
        <ChevronDown size={15} aria-hidden="true" />
      </summary>
      <div className="home-v2-footer__accordion-links">
        {group.links.map((link) => (
          <PublicPageFooterLink key={`mobile-${group.title}-${link.label}`} link={link} />
        ))}
      </div>
    </details>
  );
}

export default function PublicPageFooter() {
  return (
    <footer className="pm-v2-scope home-v2-footer" aria-label="Rodapé Private Muse">
      <div className="home-v2-footer__accent" aria-hidden="true" />
      <div className="pm-v2-container home-v2-footer__inner">
        <div className="home-v2-footer__grid">
          <div className="home-v2-footer__brand">
            <Link to="/" className="home-v2-footer__logo" aria-label="Private Muse">
              <img
                src={homeV2Asset("private-muse-logo.svg")}
                alt=""
                width="34"
                height="34"
                aria-hidden="true"
              />
              <span>Private Muse</span>
            </Link>
            <p>
              Plataforma premium para anunciantes adultos se apresentarem com elegância
              e visitantes os encontrarem com total discrição.
            </p>

            <div className="home-v2-footer__trust" aria-label="Compromissos da plataforma">
              <span>
                <ShieldCheck size={13} aria-hidden="true" />
                Perfis verificados
              </span>
              <span>
                <Lock size={13} aria-hidden="true" />
                LGPD - dados protegidos
              </span>
            </div>

            <div className="home-v2-footer__social" aria-label="Canais oficiais">
              <a href="https://www.instagram.com/privatemuse/" aria-label="Instagram">
                <Instagram size={16} aria-hidden="true" />
              </a>
              <a href={`mailto:${HOME_FOOTER_SUPPORT_EMAIL}`} aria-label="E-mail de suporte">
                <Mail size={16} aria-hidden="true" />
              </a>
            </div>
          </div>

          <nav className="home-v2-footer__desktop-nav" aria-label="Links do rodapé">
            {FOOTER_LINK_GROUPS.map((group) => (
              <PublicPageFooterGroup key={group.title} group={group} />
            ))}
          </nav>

          <nav className="home-v2-footer__mobile-nav" aria-label="Links do rodapé">
            {FOOTER_LINK_GROUPS.map((group) => (
              <PublicPageFooterAccordionGroup key={`accordion-${group.title}`} group={group} />
            ))}
          </nav>
        </div>

        <div className="home-v2-footer__legal" aria-label="Links legais">
          {FOOTER_LEGAL_LINKS.map((link) => (
            <Link key={link.label} to={link.to}>
              {link.label}
            </Link>
          ))}
        </div>

        <div className="home-v2-footer__bottom">
          <p>© 2026 Private Muse. Todos os direitos reservados.</p>
          <div className="home-v2-footer__age">
            <span>18+</span>
            <p>Plataforma exclusiva para maiores de 18 anos.</p>
          </div>
        </div>

        <p className="home-v2-footer__disclaimer">
          A Private Muse não oferece serviços de acompanhamento, não intermedeia
          transações e não participa de combinações entre usuários. Anunciantes são
          responsáveis pelo conteúdo publicado e pelo cumprimento das regras da
          plataforma.
        </p>
      </div>
      <div className="home-v2-footer__bottom-line" aria-hidden="true" />
    </footer>
  );
}
